import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import ImageLightbox from '@/components/ui/ImageLightbox';
import langoustine from '@/assets/langoustine-new.jpg';
import homard from '@/assets/homard-new.jpg';
import saumon from '@/assets/saumon-new.jpg';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';

const ProductsPreview = () => {
  const { ref, isInView } = useScrollAnimation({ margin: '-80px' as const });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const products = [
    {
      id: 1,
      name: t('products.items.langoustine.name'),
      scientific: 'Nephrops norvegicus',
      description: t('products.items.langoustine.description'),
      image: langoustine,
      featured: true,
      origin: 'Écosse',
      anchor: 'langoustine',
    },
    {
      id: 2,
      name: t('products.items.lobster.name'),
      scientific: 'Homarus gammarus',
      description: t('products.items.lobster.description'),
      image: homard,
      featured: false,
      origin: 'Europe',
      anchor: 'homard',
    },
    {
      id: 3,
      name: t('products.items.salmon.name'),
      scientific: 'Salmo salar',
      description: t('products.items.salmon.description'),
      image: saumon,
      featured: false,
      origin: 'Écosse / Norvège',
      anchor: 'saumon',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 80,
      rotateX: 15,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      rotateX: 0,
    },
  };

  const openLightbox = (e: React.MouseEvent, src: string, alt: string) => {
    e.stopPropagation();
    setLightboxImage({ src, alt });
  };

  const handleCardClick = (anchor: string) => {
    navigate(`/produits#${anchor}`);
  };

  return (
    <section ref={ref} className="section-padding bg-background overflow-hidden relative z-10">
      <div className="container-premium">
        {/* Header with blur reveal */}
        <motion.div
          initial={{ opacity: 0, y: 50, filter: 'blur(10px)' }}
          animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-accent font-medium tracking-wider uppercase text-sm"
          >
            {t('products.label')}
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6"
          >
            {t('products.title')}
          </motion.h2>
          <motion.div 
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="section-divider mb-6 origin-center" 
          />
          <motion.p 
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-muted-foreground"
          >
            {t('products.description')}
          </motion.p>
        </motion.div>

        {/* Products Grid with 3D Cards */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          style={{ perspective: '1000px' }}
        >
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              variants={cardVariants}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.15,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              whileHover={{ 
                y: -12, 
                rotateY: 2,
                transition: { duration: 0.4 } 
              }}
              className="group cursor-pointer"
              style={{ transformStyle: 'preserve-3d' }}
              onClick={() => handleCardClick(product.anchor)}
            >
              <div className="relative overflow-hidden rounded-lg bg-card shadow-lg hover:shadow-2xl transition-shadow duration-500">
                {/* Image with zoom on hover */}
                <div className="relative h-72 overflow-hidden">
                  <motion.img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    initial={{ scale: 1.3 }}
                    animate={isInView ? { scale: 1 } : { scale: 1.3 }}
                    transition={{ duration: 1.4, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent pointer-events-none" />
                  
                  {/* Zoom indicator for lightbox */}
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    onClick={(e) => openLightbox(e, product.image, product.name)}
                    className="absolute bottom-4 right-4 p-2 bg-background/80 backdrop-blur-sm rounded-full text-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-zoom-in z-10"
                    aria-label={t('products.zoomImage', { name: product.name })}
                  >
                    <ZoomIn size={18} />
                  </motion.button>
                  
                  {/* Featured Badge with pop animation */}
                  {product.featured && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0, rotate: -180 }}
                      animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                      transition={{ 
                        duration: 0.6, 
                        delay: 0.8 + index * 0.1,
                        type: 'spring',
                        stiffness: 200
                      }}
                      className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground rounded-full text-xs font-semibold"
                    >
                      <Star size={12} />
                      {t('products.signature')}
                    </motion.div>
                  )}
                  
                  {/* Origin Badge */}
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    className="absolute top-4 right-4 px-3 py-1.5 bg-background/80 backdrop-blur-sm text-foreground rounded-full text-xs font-medium"
                  >
                    {product.origin}
                  </motion.div>
                </div>

                {/* Content */}
                <motion.div 
                  className="p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.15 }}
                >
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-1 group-hover:text-accent transition-colors duration-300">
                    {product.name}
                  </h3>
                  <p className="text-sm text-accent italic mb-3">{product.scientific}</p>
                  <p className="text-muted-foreground text-sm">{product.description}</p>
                </motion.div>

                {/* Hover overlay line */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-accent"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.4 }}
                  style={{ originX: 0 }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center mt-12"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/produits">
              <Button variant="gold" size="lg">
                {t('products.viewAll')}
                <ArrowRight size={18} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <ImageLightbox
        src={lightboxImage?.src || ''}
        alt={lightboxImage?.alt || ''}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </section>
  );
};

export default ProductsPreview;
